import i18next from 'i18next';
import { toast } from 'sonner';
import { useProblemStore } from '../../store/problem.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ProblemIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchAll(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'desc', columnFilters = {}) {
        const store = useProblemStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        store.clearError();
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) acc[`filter_${key}`] = value;
                    return acc;
                }, {}),
            });
            const { data, pagination, uniqueValues } = response;
            store.setItems(data || []);
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                });
            }
            if (uniqueValues) store.setUniqueValues(uniqueValues);
            return { success: true };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadList', { defaultValue: 'Could not load list' });
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchById(id) {
        const store = useProblemStore.getState();
        store.beginDetailFetch(id);
        try {
            const response = await this.service.getById(id);
            const data = response.data;
            if (useProblemStore.getState().detailRequestId !== String(id)) {
                return { success: true, data };
            }
            store.setDetailItem(data);
            return { success: true, data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadRecord', { defaultValue: 'Could not load record' });
            if (useProblemStore.getState().detailRequestId === String(id)) {
                store.setDetailItem(null);
                store.setDetailError(msg);
                toast.error(msg);
            }
            return { success: false, error: msg };
        } finally {
            if (useProblemStore.getState().detailRequestId === String(id)) {
                store.setDetailLoading(false);
            }
        }
    }

    async create(data) {
        const store = useProblemStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('integrationToasts:recordCreated', { defaultValue: 'Record created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotCreateRecord', { defaultValue: 'Could not create record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, data) {
        const store = useProblemStore.getState();
        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('integrationToasts:recordUpdated', { defaultValue: 'Record updated' }));
            const next = response.data;
            if (next && String(store.detailItem?._id) === String(id)) {
                store.setDetailItem(next);
            }
            if (store.items?.length && next) {
                store.setItems(store.items.map((row) => (
                    String(row._id) === String(id) ? { ...row, ...next } : row
                )));
            }
            return { success: true, data: next };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotUpdateRecord', { defaultValue: 'Could not update record' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async listCandidates(params = {}) {
        try {
            const response = await this.service.listCandidates(params);
            return { success: true, data: response.data || [], pagination: response.pagination };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('itsmProblem:messages.candidatesLoadFailed', { defaultValue: 'Could not load problem suggestions' });
            toast.error(msg);
            return { success: false, data: [], error: msg };
        }
    }

    async getCandidatesSummary() {
        try {
            const response = await this.service.getCandidatesSummary();
            return { success: true, data: response.data || { openCandidates: 0 } };
        } catch (error) {
            return { success: false, data: { openCandidates: 0 }, error: getIntegrationErrorMessage(error) };
        }
    }

    async acceptCandidate(id, data = {}) {
        try {
            const response = await this.service.acceptCandidate(id, data);
            toast.success(i18next.t('itsmProblem:messages.acceptSuccess', { defaultValue: 'Problem suggestion accepted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('itsmProblem:messages.acceptFailed', { defaultValue: 'Could not accept suggestion' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async dismissCandidate(id) {
        try {
            const response = await this.service.dismissCandidate(id);
            toast.success(i18next.t('itsmProblem:messages.dismissSuccess', { defaultValue: 'Problem suggestion dismissed' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('itsmProblem:messages.dismissFailed', { defaultValue: 'Could not dismiss suggestion' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async listSuggestionsForTicket(ticketId) {
        try {
            const response = await this.service.listSuggestionsForTicket(ticketId);
            return { success: true, data: response.data || [] };
        } catch (error) {
            return { success: false, data: [], error: getIntegrationErrorMessage(error) };
        }
    }

    async listLinkedTickets(id) {
        try {
            const response = await this.service.listLinkedTickets(id);
            return { success: true, data: response.data || [] };
        } catch (error) {
            return { success: false, data: [], error: getIntegrationErrorMessage(error) };
        }
    }

    async delete(id) {
        const store = useProblemStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:recordDeleted', { defaultValue: 'Record deleted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotDeleteRecord', { defaultValue: 'Could not delete record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }
}
